import { RecordMode, type BaseObject } from './Base';
import type { Competitor } from './Traders';

export type Material = BaseObject & {
  companyId?: string;
  code: string;
  name: string;
  materialTypeId?: string;
  vatCategoryId?: string;
  mwAccountGroupId?: string;
  motherCodeId?: string;
  smallCode?: string;
  smallName?: string;
  sequence?: number;
  securityStock?: number;
  remarks?: string;
  rowStatus?: RecordMode;
  materialType?: { eType: number; id: string };
  company?: { code: string; id: string; name: string };
  vatCategory?: { code: string; id: string; name: string };
  mwAccountGroup?: { accountGroup: string; id: string; mwEntity: string };
  colorCatalogId?: string;
  sizeCatalogId?: string;
  styleCatalogId?: string;
  colorCatalog?: { code: string; id: string; name: string; printName?: string };
  sizeCatalog?: { code: string; id: string; name: string; printName?: string };
  styleCatalog?: { code: string; id: string; name: string; printName?: string };
  createUser?: { id: string; userName: string; name?: string };
  updateUser?: { id: string; userName: string; name?: string };
  materialAlterCodes?: MaterialAlterCode[];
  competitorMaterials?: CompetitorMaterial[];
  image?: string;
  allowManualPrice?: boolean;
  allowZeroValue?: boolean;
};

export type AlterCodeType = {
  id: string;
  eType: eAlterCodeType | null;
  hasMixInformation: boolean;
  prefixIncluded: boolean;
  alterCodeType?: string;
  name?: string;
  code?: string;
  isActive?: boolean;
  isFromManager?: boolean;
  mask?: string;
};

export enum eAlterCodeType {
  CompanyType = 0,
  Competitor = 1,
  Other = 2,
}

export type Assortment = {
  id: string;
  traderGroupItemID: string;
  bindingType: number | null;
  isFromManager: boolean;
  isActive: boolean;
  traderGroupItemNamePath: string;
  questionsType?: string;
};

export type AssortmentDetails = {
  id: string;
  assortmentID: string;
  mustHave?: boolean;
  isFromManager: boolean;
  isActive: boolean;
  bindingID: string;
  similarID?: string | null;
  description: string;
  minQuantity?: number | null;
};

export type AssortmentSet = {
  id: string;
  name: string;
  minQuantity?: number;
  items: AssortmentDetails[];
};

export enum eQuestionsType {
  Material_groups = 1,
  Materials = 2,
}

export enum eMaterialType {
  Item,
  Service,
  Special,
  Package,
  Kit,
  Ticket,
  CompetitorMaterial,
}

export type MaterialType = {
  id: string;
  eType: eMaterialType;
  code: string;
  name: string;
};

export type MaterialAlterCode = {
  id: string;
  isActive: boolean;
  isFromManager: boolean;
  boid?: string;
  alterCode: AlterCode;
  measurementUnit: MeasurementUnit;
  measurementUnitId?: string;
  deleted?: boolean;
  alterCodeId?: string;
  isDefault?: boolean;
  quantityTotalizer?: boolean;
  relationFactor?: number;
  minSalesQuantity?: number;
  shortCode?: string;
  lotStart?: string;
  lotFinsh?: string;
  dimension1?: number;
  dimension2?: number;
  dimension3?: number;
  materialId?: string;
  recordMode?: RecordMode;
  material?: {
    allowManualPrice?: boolean;
    allowZeroValue?: boolean;
    image?: string;
  };
  sequence?: number;
};

export type MaterialAlterCodeForSalesDocument = {
  id: string;
  materialId: string;
  material?: {
    allowManualPrice?: boolean;
    allowZeroValue?: boolean;
    image?: string;
  };
  alterCode?: AlterCode;
  alterCodeId?: string;
  relationFactor?: number;
  recordMode?: RecordMode;
  sequence?: number;
  measurementUnitId?: string;
  measurementUnit?: MeasurementUnit;
  allMaterialAlterCodes: SiblingMaterialAlterCodeForSalesDocument[];
};

export type SiblingMaterialAlterCodeForSalesDocument = {
  id: string;
  isDefault: boolean;
  materialId: string;
  alterCode?: AlterCode;
  relationFactor: number;
  sequence?: number;
  measurementUnitId: string;
  measurementUnit: MeasurementUnit;
};

export type AlterCode = {
  id: string;
  code: string;
  name: string;
  isActive?: boolean;
  isFromManager?: boolean;
  alterCodeTypeId?: string;
  alterCodeType?: AlterCodeType;
  recordMode?: RecordMode;
  deleted?: boolean;
};

export type MeasurementUnit = {
  id: string;
  code: string;
  name: string;
};

export type CompetitorMaterial = {
  id: string;
  isActive: boolean;
  isFromManager: boolean;
  isDefault: boolean;
  boid?: string;
  materialAlterCode: MaterialAlterCode;
  competitor: Partial<Competitor>;
  competitorId?: string;
  materialId?: string;
  material?: Partial<Material>;
  recordMode?: RecordMode;
};

export type MaterialLastVisitValue = BaseObject & {
  materialId: string;
  competitorId?: string;
  traderSiteId: string;
  alterCodeId: string;
  customerId?: string;
  material: { id: string; code: string; name: string };
  competitor?: { id: string; code: string };
  traderSite: { id: string; code: string; name: string };
  alterCode: { id: string; code: string; name: string };
  customer?: { id: string; code: string; name: string };
  lastPrice?: number;
  lastDate?: string;
  alterDescription?: string;
  shelf?: string;
  faces?: number;
  remarks?: string;
  havePromo: boolean;
  notExist: boolean;
};
